/**
 * ERB Cart Free Gifts, Member Prompt, and Member Code Handler
 * Eat Right Basket
 */
(function () {
  'use strict';

  const DEFAULT_CONFIG = {
    enabled: true,
    autoApplyMemberCode: false,
    memberCode: 'ERBMEMBER',
    isMember: false,
    isLoggedIn: false,
    memberGift: {
      handle: 'free-premium-sample-complimentary-gift',
      sku: 'SS/FREE/SAMPLE',
      variantId: null,
      title: 'Free Premium Sample',
      image: '',
    },
    nonMemberGift: {
      handle: 'free-mystery-gift',
      sku: 'SS/FREE/GIFT',
      variantId: null,
      title: 'Free Mystery Gift',
      image: '',
    },
    memberThreshold: 99900, // ₹999 in paise/cents
    nonMemberThreshold: 150000, // ₹1,500 in paise/cents
    memberSavingsNote: 'Members save more + 3% discount',
    loginUrl: '/account/login?return_to=' + encodeURIComponent('/cart'),
  };

  const config = Object.assign({}, DEFAULT_CONFIG, window.ERB_CART_CONFIG || {});

  let isSyncing = false;
  let pendingSync = false;
  let lastKnownCart = null;

  /**
   * Check if a line item is any gift item
   */
  function isGiftItem(item) {
    if (!item) return false;
    const handle = item.handle || (item.product && item.product.handle);
    const sku = item.sku;
    const variantId = item.variant_id || item.id;
    const props = item.properties || {};

    if (props._is_gift === 'true' || props._is_gift === true) return true;

    if (handle && (handle === config.memberGift.handle || handle === config.nonMemberGift.handle)) {
      return true;
    }
    if (sku && (sku === config.memberGift.sku || sku === config.nonMemberGift.sku)) {
      return true;
    }
    if (config.memberGift.variantId && String(variantId) === String(config.memberGift.variantId)) {
      return true;
    }
    if (config.nonMemberGift.variantId && String(variantId) === String(config.nonMemberGift.variantId)) {
      return true;
    }
    return false;
  }

  /**
   * Check if a line item is the Member Gift
   */
  function isMemberGift(item) {
    if (!item) return false;
    const handle = item.handle || (item.product && item.product.handle);
    const sku = item.sku;
    const variantId = item.variant_id || item.id;

    if (handle && handle === config.memberGift.handle) return true;
    if (sku && sku === config.memberGift.sku) return true;
    if (config.memberGift.variantId && String(variantId) === String(config.memberGift.variantId)) return true;
    return false;
  }

  /**
   * Check if a line item is the Non-Member Gift
   */
  function isNonMemberGift(item) {
    if (!item) return false;
    const handle = item.handle || (item.product && item.product.handle);
    const sku = item.sku;
    const variantId = item.variant_id || item.id;

    if (handle && handle === config.nonMemberGift.handle) return true;
    if (sku && sku === config.nonMemberGift.sku) return true;
    if (config.nonMemberGift.variantId && String(variantId) === String(config.nonMemberGift.variantId)) return true;
    return false;
  }

  /**
   * Calculate qualifying cart total (after all discounts, excluding gift lines)
   */
  function getQualifyingTotal(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    let total = 0;
    cart.items.forEach(function (item) {
      if (!isGiftItem(item)) {
        const itemPrice = typeof item.final_line_price !== 'undefined' ? item.final_line_price : item.line_price;
        total += itemPrice;
      }
    });
    return total;
  }

  /**
   * Determine target gift type: 'member', 'non_member', or 'none'
   */
  function determineTargetGift(cart) {
    const qualifyingTotal = getQualifyingTotal(cart);
    if (config.isMember) {
      // Logged in ERB Circle member:
      // ≥ ₹999 -> Premium Sample (qty 1). NO Mystery Gift even above ₹1,500!
      if (qualifyingTotal >= config.memberThreshold) {
        return 'member';
      }
      return 'none';
    } else {
      // Guest or logged in without erb-circle:
      // ≥ ₹1,500 -> Mystery Gift (qty 1).
      if (qualifyingTotal >= config.nonMemberThreshold) {
        return 'non_member';
      }
      return 'none';
    }
  }

  /**
   * Resolve variant ID for a gift product if not provided via Liquid
   */
  async function resolveGiftVariantId(targetGiftType) {
    const giftConfig = targetGiftType === 'member' ? config.memberGift : config.nonMemberGift;
    if (giftConfig.variantId) return giftConfig.variantId;

    if (giftConfig._resolvingPromise) {
      return giftConfig._resolvingPromise;
    }

    giftConfig._resolvingPromise = fetch('/products/' + giftConfig.handle + '.js')
      .then(function (res) {
        if (!res.ok) throw new Error('Product not found: ' + giftConfig.handle);
        return res.json();
      })
      .then(function (prod) {
        if (prod && prod.variants && prod.variants.length > 0) {
          giftConfig.variantId = prod.variants[0].id;
          if (prod.variants[0].sku && !giftConfig.sku) {
            giftConfig.sku = prod.variants[0].sku;
          }
          if (prod.title) giftConfig.title = prod.title;
          if (prod.featured_image) giftConfig.image = prod.featured_image;
          return giftConfig.variantId;
        }
        return null;
      })
      .catch(function (err) {
        console.warn('[ERB Cart Gifts] Could not resolve variant ID for ' + giftConfig.handle, err);
        return null;
      });

    return giftConfig._resolvingPromise;
  }

  /**
   * Apply automatic ERBMEMBER discount code if setting is enabled
   */
  function checkAutoApplyMemberCode(cart) {
    if (!config.autoApplyMemberCode) return;
    if (!config.isMember) return;
    if (!cart || cart.item_count === 0) return;

    const alreadyApplied = sessionStorage.getItem('erb_member_code_applied');
    if (alreadyApplied) return;

    fetch('/discount/' + encodeURIComponent(config.memberCode))
      .then(function () {
        sessionStorage.setItem('erb_member_code_applied', 'true');
      })
      .catch(function (err) {
        console.warn('[ERB Cart Gifts] Could not auto-apply member code', err);
      });
  }

  /**
   * Update visibility of "Member? Log in" prompt
   * Visible when user is not logged in and cart is not empty
   */
  function updateMemberPrompt(cart) {
    const prompts = document.querySelectorAll('[data-erb-member-prompt], .memlogin');
    if (!prompts.length) return;

    if (config.isLoggedIn) {
      prompts.forEach(function (el) {
        el.style.display = 'none';
      });
      return;
    }

    const hasItems = cart && typeof cart.item_count !== 'undefined' ? cart.item_count > 0 : false;

    prompts.forEach(function (el) {
      el.style.display = hasItems ? '' : 'none';
      const textEl = el.querySelector('[data-erb-member-prompt-text]') || el.querySelector('span');
      if (textEl && cart) {
        const total = typeof cart.total_price !== 'undefined' ? cart.total_price : 0;
        if (total >= config.memberThreshold) {
          textEl.textContent = 'ERB Circle member? Log in to get your member price and a free premium sample.';
        } else {
          textEl.textContent = 'ERB Circle member? Log in to get your member discount.';
        }
      }
    });
  }

  /**
   * Check if a line item is a membership product
   */
  function isMembershipItem(item) {
    if (!item) return false;
    const handle = (item.handle || (item.product && item.product.handle) || '').toLowerCase();
    const title = (item.title || (item.product && item.product.title) || item.product_title || '').toLowerCase();
    const url = (item.url || '').toLowerCase();
    const sku = (item.sku || '').toLowerCase();
    return (
      handle.includes('membership') ||
      handle.includes('aarambh') ||
      handle.includes('vishwaas') ||
      handle.includes('vishwas') ||
      handle.includes('parivaar') ||
      handle.includes('parivar') ||
      title.includes('membership') ||
      title.includes('circle') ||
      title.includes('aarambh') ||
      title.includes('vishwaas') ||
      title.includes('vishwas') ||
      title.includes('parivaar') ||
      title.includes('parivar') ||
      url.includes('membership') ||
      url.includes('aarambh') ||
      url.includes('vishwaas') ||
      url.includes('vishwas') ||
      url.includes('parivaar') ||
      url.includes('parivar') ||
      sku.includes('membership') ||
      sku.includes('circle')
    );
  }

  /**
   * Check if cart currently contains any membership product
   */
  function hasMembershipInCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return false;
    return cart.items.some(function (item) {
      return isMembershipItem(item);
    });
  }

  /**
   * Calculate qualifying products total (excluding gifts and membership items)
   */
  function getProductQualifyingTotal(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    let total = 0;
    cart.items.forEach(function (item) {
      if (!isGiftItem(item) && !isMembershipItem(item)) {
        const itemPrice = typeof item.final_line_price !== 'undefined' ? item.final_line_price : item.line_price;
        total += itemPrice;
      }
    });
    return total;
  }

  /**
   * Update visibility and dynamic savings of ERB Circle Membership upsell card
   * Visible for non-members / not-logged-in users when cart is not empty and no membership is in cart
   */
  function updateMembershipCard(cart) {
    const cards = document.querySelectorAll('[data-erb-membership-card], .ccard');
    if (!cards.length) return;

    const isMember = config.isMember;
    const hasMembership = hasMembershipInCart(cart);
    const itemCount = cart && typeof cart.item_count !== 'undefined' ? cart.item_count : 0;
    const productTotalCents = getProductQualifyingTotal(cart);

    // Hide if customer is already a member, or membership is in cart, or cart is empty, or no qualifying items
    if (isMember || hasMembership || itemCount === 0 || productTotalCents <= 0) {
      cards.forEach(function (card) {
        card.style.display = 'none';
      });
      return;
    }

    // Dynamic savings calculation:
    // 1% member discount (min ₹1)
    const qualifyingRupees = productTotalCents / 100;
    let memberDiscount = Math.round(qualifyingRupees * 0.01);
    if (memberDiscount < 1 && qualifyingRupees > 0) {
      memberDiscount = 1;
    }

    // Delivery savings: If productTotalCents >= ₹499 (49900) and < ₹999 (99900), delivery fee of ₹40 is saved
    let deliverySavings = 0;
    let deliveryBullet = 'Free delivery from ₹499 on every order';
    if (productTotalCents >= 49900 && productTotalCents < 99900) {
      deliverySavings = 40;
      deliveryBullet = 'Free delivery on this order (from ₹499)';
    }

    // 3% extra discount (min ₹1)
    let extraDiscount = Math.round(qualifyingRupees * 0.03);
    if (extraDiscount < 1 && qualifyingRupees > 0) {
      extraDiscount = 1;
    }

    // Total savings
    const totalSavings = memberDiscount + deliverySavings + extraDiscount;

    cards.forEach(function (card) {
      card.style.display = '';

      const savingsEl = card.querySelector('[data-erb-savings]');
      if (savingsEl) savingsEl.textContent = '₹' + totalSavings;

      const memberDiscountEl = card.querySelector('[data-erb-member-discount]');
      if (memberDiscountEl) memberDiscountEl.textContent = '₹' + memberDiscount;

      const deliveryBulletEl = card.querySelector('[data-erb-delivery-bullet]');
      if (deliveryBulletEl) deliveryBulletEl.textContent = deliveryBullet;

      const extraDiscountEl = card.querySelector('[data-erb-extra-discount]');
      if (extraDiscountEl) extraDiscountEl.textContent = '₹' + extraDiscount;
    });
  }

  const PLAN_HANDLES = {
    'plan-aarambh': 'aarambh-3-months-the-beginning-membership-plan',
    'plan-vishwaas': 'vishwaas-6-months-the-trust-membership-plan',
    'plan-parivaar': 'parivaar-12-months-the-family-membership-plan',
  };

  const planVariantCache = {};

  /**
   * Resolve variant ID for a specific membership plan
   */
  async function resolveMembershipVariantId(planKey, card) {
    const key = planKey || 'plan-aarambh';
    if (planVariantCache[key]) return planVariantCache[key];

    if (card) {
      if (key === 'plan-aarambh') {
        const id = card.getAttribute('data-aarambh-id');
        if (id && id !== '0' && id !== 'null' && id !== 'undefined' && id !== '') {
          planVariantCache[key] = id;
          return id;
        }
      } else if (key === 'plan-vishwaas') {
        const id = card.getAttribute('data-vishwaas-id');
        if (id && id !== '0' && id !== 'null' && id !== 'undefined' && id !== '') {
          planVariantCache[key] = id;
          return id;
        }
      } else if (key === 'plan-parivaar') {
        const id = card.getAttribute('data-parivaar-id');
        if (id && id !== '0' && id !== 'null' && id !== 'undefined' && id !== '') {
          planVariantCache[key] = id;
          return id;
        }
      }
    }

    const handle = PLAN_HANDLES[key] || PLAN_HANDLES['plan-aarambh'];
    try {
      const res = await fetch('/products/' + handle + '.js');
      if (res.ok) {
        const prod = await res.json();
        if (prod && prod.variants && prod.variants.length > 0) {
          planVariantCache[key] = prod.variants[0].id;
          return prod.variants[0].id;
        }
      }
    } catch (e) {
      console.warn('[ERB Cart] Could not resolve variant ID for ' + key, e);
    }
    return null;
  }

  async function resolveAarambhVariantId(card) {
    return resolveMembershipVariantId('plan-aarambh', card);
  }

  /**
   * Enhance gift rows in the DOM (both Cart Drawer and Cart Page):
   * - Shows "FREE" instead of ₹0.00
   * - Hides/disables quantity selector
   * - Hides/omits remove button
   * - Disables click on gift product link if unlisted
   */
  function enhanceGiftElementsInDOM() {
    // 1. Mini cart items (Drawer)
    const miniItems = document.querySelectorAll('.mini-cart-item');
    miniItems.forEach(function (itemEl) {
      const link = itemEl.querySelector('.mini-cart-meta a') || itemEl.querySelector('a');
      const href = link ? link.getAttribute('href') || '' : '';
      const text = itemEl.textContent || '';
      const isMember = href.includes(config.memberGift.handle) || text.includes('Premium Sample');
      const isNonMember = href.includes(config.nonMemberGift.handle) || text.includes('Mystery Gift');

      if (isMember || isNonMember) {
        itemEl.setAttribute('data-is-gift', 'true');

        // Replace price text with FREE
        const priceSpans = itemEl.querySelectorAll('.mini-cart-meta span, .mini-cart-item-price');
        priceSpans.forEach(function (span) {
          if (
            span.textContent.includes('₹0') ||
            span.textContent.includes('0.00') ||
            span.classList.contains('mini-cart-item-price')
          ) {
            span.innerHTML = '<span class="erb-gift-badge">FREE</span>';
          }
        });

        // Hide quantity controls and remove button
        const qtyBox = itemEl.querySelector('.js-qty');
        if (qtyBox) {
          qtyBox.style.display = 'none';
          if (!itemEl.querySelector('.erb-gift-qty-tag')) {
            const tag = document.createElement('span');
            tag.className = 'erb-gift-qty-tag';
            tag.textContent = 'Qty: 1';
            qtyBox.parentNode.insertBefore(tag, qtyBox);
          }
        }

        const removeBtn = itemEl.querySelector('.js-remove-mini-cart');
        if (removeBtn) {
          removeBtn.style.display = 'none';
        }

        // Prevent navigation to unlisted gift product page
        if (link) {
          link.removeAttribute('href');
          link.style.cursor = 'default';
        }
      }

      const isGift = isMember || isNonMember || itemEl.getAttribute('data-is-gift') === 'true';
      const isMembership = isMembershipItem({
        url: href,
        title: text,
      });

      // Handle non-member savings note (placed below "Price x Qty" and above stepper)
      const existingNote = itemEl.querySelector('.erb-member-savings-note');
      if (!config.isMember && !isGift && !isMembership) {
        const meta = itemEl.querySelector('.mini-cart-meta');
        if (meta) {
          const qtyBox = meta.querySelector('.d-flex.align-items-center') || meta.querySelector('.js-qty, .erb-gift-qty-tag');
          if (qtyBox) {
            const target = qtyBox.closest('.d-flex') || qtyBox;
            if (existingNote) {
              if (existingNote.nextElementSibling !== target) {
                meta.insertBefore(existingNote, target);
              }
            } else {
              const noteEl = document.createElement('div');
              noteEl.className = 'erb-member-savings-note';
              noteEl.textContent = config.memberSavingsNote || 'Members save more + 3% discount';
              meta.insertBefore(noteEl, target);
            }
          } else if (!existingNote) {
            const noteEl = document.createElement('div');
            noteEl.className = 'erb-member-savings-note';
            noteEl.textContent = config.memberSavingsNote || 'Members save more + 3% discount';
            meta.appendChild(noteEl);
          }
        }
      } else if (existingNote) {
        existingNote.remove();
      }
    });

    // 2. Cart Page table rows
    const cartRows = document.querySelectorAll('.cart__row.responsive-table__row');
    cartRows.forEach(function (row) {
      const link = row.querySelector('a[href*="/products/"]');
      const href = link ? link.getAttribute('href') || '' : '';
      const text = row.textContent || '';
      const isMember = href.includes(config.memberGift.handle) || text.includes('Premium Sample');
      const isNonMember = href.includes(config.nonMemberGift.handle) || text.includes('Mystery Gift');

      if (isMember || isNonMember) {
        row.setAttribute('data-is-gift', 'true');

        // Replace price columns with FREE
        const totals = row.querySelectorAll('.cart__item-total, .cart__cell--total');
        totals.forEach(function (tot) {
          if (!tot.querySelector('.erb-gift-badge')) {
            tot.innerHTML = '<span class="erb-gift-badge">FREE</span>';
          }
        });

        // Quantity controls
        const qtyInputs = row.querySelectorAll('input.cart__quantity, .qty-mobile');
        qtyInputs.forEach(function (inp) {
          inp.style.display = 'none';
        });

        const qtyCells = row.querySelectorAll('.cart__cell--quantity');
        qtyCells.forEach(function (cell) {
          if (!cell.querySelector('.erb-gift-qty-tag')) {
            cell.innerHTML = '<span class="erb-gift-qty-tag">1</span>';
          }
        });

        // Remove links
        const removeLinks = row.querySelectorAll('a[href*="/cart/change?line="], .cart-item__remove');
        removeLinks.forEach(function (rem) {
          rem.style.display = 'none';
        });

        if (link) {
          link.removeAttribute('href');
          link.style.cursor = 'default';
        }
      }

      const isGift = isMember || isNonMember || row.getAttribute('data-is-gift') === 'true';
      const isMembership = isMembershipItem({
        url: href,
        title: text,
      });

      // Handle non-member savings note on cart page
      const existingNotes = row.querySelectorAll('.erb-member-savings-note');
      if (!config.isMember && !isGift && !isMembership) {
        if (existingNotes.length === 0) {
          const noteText = config.memberSavingsNote || 'Members save more + 3% discount';
          const mobileCol = row.querySelector('.cart__cell--image .d-flex.d-lg-none');
          if (mobileCol) {
            const mNote = document.createElement('div');
            mNote.className = 'erb-member-savings-note';
            mNote.textContent = noteText;
            const targetEl = mobileCol.querySelector('.cart__item-total') || mobileCol.querySelector('.qty-mobile');
            if (targetEl) {
              mobileCol.insertBefore(mNote, targetEl.closest('div') || targetEl);
            } else {
              mobileCol.appendChild(mNote);
            }
          }
          const desktopCol = row.querySelector('td.d-none.d-lg-table-cell');
          if (desktopCol) {
            const dNote = document.createElement('div');
            dNote.className = 'erb-member-savings-note';
            dNote.textContent = noteText;
            const removeP = desktopCol.querySelector('p:has(a[href*="/cart/change"])') || desktopCol.querySelector('a[href*="/cart/change"]');
            if (removeP) {
              desktopCol.insertBefore(dNote, removeP.closest('p') || removeP);
            } else {
              desktopCol.appendChild(dNote);
            }
          }
        }
      } else if (existingNotes.length > 0) {
        existingNotes.forEach(function (n) { n.remove(); });
      }
    });
  }

  /**
   * Synchronize cart gifts with rules
   */
  async function syncGifts(passedCart) {
    if (!config.enabled) return;
    if (isSyncing) {
      pendingSync = true;
      return;
    }

    isSyncing = true;
    try {
      let cart = passedCart;
      if (!cart || !Array.isArray(cart.items)) {
        const res = await fetch('/cart.js');
        if (!res.ok) throw new Error('Could not fetch cart.js');
        cart = await res.json();
      }

      lastKnownCart = cart;
      checkAutoApplyMemberCode(cart);
      updateMemberPrompt(cart);
      updateMembershipCard(cart);

      const targetGift = determineTargetGift(cart);
      const items = cart.items || [];

      let memberGiftLines = [];
      let nonMemberGiftLines = [];

      items.forEach(function (item, index) {
        if (isMemberGift(item)) {
          memberGiftLines.push({ item: item, line: index + 1 });
        } else if (isNonMemberGift(item)) {
          nonMemberGiftLines.push({ item: item, line: index + 1 });
        }
      });

      const updates = {};
      let needsUpdate = false;
      let needAddGift = false;

      if (targetGift === 'member') {
        // 1. Remove all non-member gifts
        nonMemberGiftLines.forEach(function (g) {
          updates[g.item.key || g.item.variant_id] = 0;
          needsUpdate = true;
        });

        // 2. Member gift must have qty 1
        if (memberGiftLines.length === 0) {
          needAddGift = true;
        } else {
          // If first has qty != 1, set to 1
          if (memberGiftLines[0].item.quantity !== 1) {
            updates[memberGiftLines[0].item.key || memberGiftLines[0].item.variant_id] = 1;
            needsUpdate = true;
          }
          // If multiple member gift lines exist, remove extras
          for (let i = 1; i < memberGiftLines.length; i++) {
            updates[memberGiftLines[i].item.key || memberGiftLines[i].item.variant_id] = 0;
            needsUpdate = true;
          }
        }
      } else if (targetGift === 'non_member') {
        // 1. Remove all member gifts
        memberGiftLines.forEach(function (g) {
          updates[g.item.key || g.item.variant_id] = 0;
          needsUpdate = true;
        });

        // 2. Non-member gift must have qty 1
        if (nonMemberGiftLines.length === 0) {
          needAddGift = true;
        } else {
          if (nonMemberGiftLines[0].item.quantity !== 1) {
            updates[nonMemberGiftLines[0].item.key || nonMemberGiftLines[0].item.variant_id] = 1;
            needsUpdate = true;
          }
          for (let i = 1; i < nonMemberGiftLines.length; i++) {
            updates[nonMemberGiftLines[i].item.key || nonMemberGiftLines[i].item.variant_id] = 0;
            needsUpdate = true;
          }
        }
      } else {
        // targetGift === 'none': Remove ALL gifts
        memberGiftLines.forEach(function (g) {
          updates[g.item.key || g.item.variant_id] = 0;
          needsUpdate = true;
        });
        nonMemberGiftLines.forEach(function (g) {
          updates[g.item.key || g.item.variant_id] = 0;
          needsUpdate = true;
        });
      }

      let cartChanged = false;

      // Apply updates (removals / quantity adjustments)
      if (needsUpdate) {
        const updateRes = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: updates }),
        });
        if (updateRes.ok) {
          cart = await updateRes.json();
          lastKnownCart = cart;
          cartChanged = true;
        }
      }

      // Add target gift if required
      if (needAddGift && targetGift !== 'none') {
        const variantId = await resolveGiftVariantId(targetGift);
        if (variantId) {
          const addRes = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: [
                {
                  id: Number(variantId),
                  quantity: 1,
                  properties: { _is_gift: 'true' },
                },
              ],
            }),
          });
          if (addRes.ok) {
            const cartRes = await fetch('/cart.js');
            if (cartRes.ok) {
              cart = await cartRes.json();
              lastKnownCart = cart;
              cartChanged = true;
            }
          }
        } else {
          console.warn('[ERB Cart Gifts] Cannot add gift: variant ID could not be resolved.');
        }
      }

      updateMemberPrompt(cart);
      updateMembershipCard(cart);
      enhanceGiftElementsInDOM();

      if (cartChanged) {
        // Trigger theme updates
        if (window.theme && window.theme.miniCart) {
          if (typeof window.theme.miniCart.updateElements === 'function') {
            window.theme.miniCart.updateElements();
          }
          if (typeof window.theme.miniCart.generateCart === 'function') {
            window.theme.miniCart.generateCart();
          }
        }

        // Update cart count elements
        document.querySelectorAll('.js-cart-count').forEach(function (el) {
          el.textContent = cart.item_count;
          if (cart.item_count === 0) {
            el.classList.add('is-zero');
          } else {
            el.classList.remove('is-zero');
          }
        });

        // Dispatch standard events
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart: cart } }));

        // If on cart page and cart is now empty or has significant changes, reload or update table
        if (window.location.pathname.includes('/cart')) {
          if (cart.item_count === 0) {
            window.location.reload();
          }
        }
      }
    } catch (err) {
      console.error('[ERB Cart Gifts] Error syncing gifts:', err);
    } finally {
      isSyncing = false;
      if (pendingSync) {
        pendingSync = false;
        syncGifts();
      }
    }
  }

  /**
   * Misuse Protection: Validate checkout before proceeding
   */
  async function validateCheckoutBeforeProceed(e) {
    if (!config.enabled) return;

    try {
      const res = await fetch('/cart.js');
      if (!res.ok) return;
      const cart = await res.json();
      const targetGift = determineTargetGift(cart);

      let hasInvalidGift = false;
      (cart.items || []).forEach(function (item) {
        if (targetGift === 'none' && isGiftItem(item)) {
          hasInvalidGift = true;
        } else if (targetGift === 'member' && isNonMemberGift(item)) {
          hasInvalidGift = true;
        } else if (targetGift === 'non_member' && isMemberGift(item)) {
          hasInvalidGift = true;
        } else if (isGiftItem(item) && item.quantity > 1) {
          hasInvalidGift = true;
        }
      });

      if (hasInvalidGift) {
        e.preventDefault();
        e.stopImmediatePropagation();
        await syncGifts(cart);
        // Re-navigate to checkout once sanitized
        window.location.href = '/checkout';
      }
    } catch (err) {
      console.warn('[ERB Cart Gifts] Validation error:', err);
    }
  }

  // Intercept checkout triggers
  document.addEventListener(
    'click',
    function (e) {
      const checkoutBtn = e.target.closest(
        '[name="checkout"], .js-cart-btn-checkout, .btn-cart-checkout, a[href*="/checkout"], [data-gokwik-checkout]',
      );
      if (checkoutBtn) {
        validateCheckoutBeforeProceed(e);
      }
    },
    true,
  );

  // Intercept AJAX fetch requests to /cart/...
  const originalFetch = window.fetch;
  window.fetch = function () {
    const url = arguments[0];
    const isCartReq =
      typeof url === 'string' &&
      (url.includes('/cart/add') ||
        url.includes('/cart/change') ||
        url.includes('/cart/update') ||
        url.includes('/cart/clear'));

    return originalFetch.apply(this, arguments).then(function (response) {
      if (isCartReq && response.ok && !isSyncing) {
        setTimeout(function () {
          syncGifts();
        }, 150);
      }
      return response;
    });
  };

  // Intercept jQuery AJAX if available
  if (window.jQuery) {
    window.jQuery(document).ajaxSuccess(function (event, xhr, settings) {
      if (settings && settings.url && typeof settings.url === 'string') {
        if (
          settings.url.includes('/cart/add') ||
          settings.url.includes('/cart/change') ||
          settings.url.includes('/cart/update') ||
          settings.url.includes('/cart/clear')
        ) {
          if (!isSyncing) {
            setTimeout(function () {
              syncGifts();
            }, 150);
          }
        }
      }
    });
  }

  // Listen to cart events
  document.addEventListener('cart:updated', function (e) {
    if (!isSyncing && e.detail && e.detail.cart) {
      setTimeout(function () {
        enhanceGiftElementsInDOM();
      }, 50);
    }
  });

  // Handle Member Login button click
  document.addEventListener('click', function (e) {
    const loginBtn = e.target.closest('[data-mem-login]');
    if (!loginBtn) return;

    e.preventDefault();
    window.location.href = config.loginUrl;
  });

  // Handle "See 6 and 12 month plans" toggle
  document.addEventListener('click', function (e) {
    const moreBtn = e.target.closest('[data-more-plans]');
    if (!moreBtn) return;

    e.preventDefault();
    const card = moreBtn.closest('[data-erb-membership-card], .ccard');
    if (!card) return;

    const plansDiv = card.querySelector('.ccard__plans');
    if (!plansDiv) return;

    if (plansDiv.hasAttribute('hidden')) {
      plansDiv.removeAttribute('hidden');
      moreBtn.textContent = 'Hide plans';
    } else {
      plansDiv.setAttribute('hidden', '');
      moreBtn.textContent = 'See 6 and 12 month plans';
    }
  });

  // Handle "Add membership" CTA button and multi-plan selection
  document.addEventListener('click', async function (e) {
    const planBtn = e.target.closest('[data-plan], [data-erb-add-membership]');
    if (!planBtn) return;
    if (planBtn.hasAttribute('data-more-plans')) return;

    e.preventDefault();
    if (planBtn.classList.contains('is-loading')) return;

    const card = planBtn.closest('[data-erb-membership-card], .ccard');
    const planKey = planBtn.getAttribute('data-plan') || 'plan-aarambh';

    planBtn.classList.add('is-loading');

    const statusEl = planBtn.querySelector('.ccard__plan-status, .ccard__btn-status');
    const priceEl = planBtn.querySelector('b');
    const labelEl = planBtn.querySelector('.ccard__btn-label');

    if (statusEl) statusEl.style.display = 'inline-block';
    if (priceEl) priceEl.style.display = 'none';
    if (labelEl) labelEl.style.display = 'none';

    function resetBtnState() {
      planBtn.classList.remove('is-loading');
      if (statusEl) statusEl.style.display = 'none';
      if (priceEl) priceEl.style.display = '';
      if (labelEl) labelEl.style.display = '';
    }

    try {
      let variantId = planBtn.getAttribute('data-variant-id');
      if (!variantId || variantId === '0' || variantId === 'null') {
        variantId = await resolveMembershipVariantId(planKey, card);
      }

      if (!variantId) {
        throw new Error('Membership variant ID not resolved for ' + planKey);
      }

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
            },
          ],
        }),
      });

      if (res.ok) {
        await syncGifts();

        if (card) {
          card.style.display = 'none';
        }

        if (window.theme && window.theme.miniCart) {
          if (typeof window.theme.miniCart.open === 'function') {
            window.theme.miniCart.open();
          }
          if (typeof window.theme.miniCart.generateCart === 'function') {
            window.theme.miniCart.generateCart();
          }
          if (typeof window.theme.miniCart.updateElements === 'function') {
            window.theme.miniCart.updateElements();
          }
        }

        if (window.location.pathname.includes('/cart')) {
          window.location.reload();
        }
      } else {
        const errData = await res.json().catch(function () {
          return {};
        });
        console.error('[ERB Cart] Failed to add membership to cart:', errData);
      }
    } catch (err) {
      console.error('[ERB Cart] Error adding membership:', err);
    } finally {
      resetBtnState();
    }
  });

  // Watch for DOM changes to enhance dynamically inserted cart items
  const observer = new MutationObserver(function () {
    enhanceGiftElementsInDOM();
  });

  function initObserver() {
    const drawer = document.querySelector('.mini-cart-content') || document.querySelector('.js-mini-cart');
    const cartTable = document.getElementById('CartProducts');
    if (drawer) observer.observe(drawer, { childList: true, subtree: true });
    if (cartTable) observer.observe(cartTable, { childList: true, subtree: true });
  }

  // Initial runs
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initObserver();
      syncGifts();
      enhanceGiftElementsInDOM();
    });
  } else {
    initObserver();
    syncGifts();
    enhanceGiftElementsInDOM();
  }

  window.addEventListener('pageshow', function () {
    syncGifts();
  });

  // Export to window
  window.ERB_CART = {
    syncCart: syncGifts,
    isGiftItem: isGiftItem,
    isMemberGift: isMemberGift,
    isNonMemberGift: isNonMemberGift,
    isMembershipItem: isMembershipItem,
    hasMembershipInCart: hasMembershipInCart,
    getQualifyingTotal: getQualifyingTotal,
    getProductQualifyingTotal: getProductQualifyingTotal,
    determineTargetGift: determineTargetGift,
    enhanceGiftElementsInDOM: enhanceGiftElementsInDOM,
    updateMembershipCard: updateMembershipCard,
    updateMemberPrompt: updateMemberPrompt,
    resolveMembershipVariantId: resolveMembershipVariantId,
  };
})();
